/**
 * kaleidoscopePipeline — 6-fold symmetric mirror with audio-reactive rotation.
 *
 * Folds the screen into a triangular wedge and mirrors it 6 times
 * around the center. The input texture is procedural: a high-frequency
 * sin-based pattern. The wedge rotates with time, and the mirror
 * creates symmetric patterns. Audio drives rotation speed and color
 * saturation.
 *
 * The result looks like a real kaleidoscope — radial symmetry, complex
 * organic patterns, ever-shifting.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface KaleidoscopePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

// Source pattern — a complex multi-frequency field that gets mirrored.
vec3 source(vec2 p) {
  float t = uTime;
  vec2 distorted = p + vec2(
    fbm(p * 2.0 + vec2(t * 0.3, 0.0)),
    fbm(p * 2.0 + vec2(0.0, t * 0.3))
  ) * 0.5;
  float v = fbm(distorted * 3.0 + vec2(t * 0.5, 0.0));
  // Color: cool to warm gradient driven by noise + time.
  vec3 c1 = vec3(0.10, 0.05, 0.25);
  vec3 c2 = vec3(0.95, 0.40, 0.10);
  vec3 c3 = vec3(0.20, 0.85, 0.75);
  return mix(c1, c2, smoothstep(0.3, 0.7, v)) +
         mix(vec3(0.0), c3, smoothstep(0.6, 0.85, v)) * 0.5;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Screen coords, centered, aspect-corrected.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Rotate with time (audio drives speed).
  float angle = uTime * (0.3 + bass * 0.8);
  float ca = cos(angle), sa = sin(angle);
  p = mat2(ca, -sa, sa, ca) * p;

  // Polar coords.
  float r = length(p);
  float theta = atan(p.y, p.x);

  // Fold theta into a 60° wedge (PI/3) — this is the kaleidoscope fold.
  // 6-fold symmetry means we fold into 1/6 of the full circle.
  const float PI = 3.14159265;
  float wedgeAngle = PI / 3.0;
  // Number of folds = 6.
  theta = mod(theta + wedgeAngle * 0.5, wedgeAngle);
  // Mirror: if theta > half-wedge, reflect it.
  if (theta > wedgeAngle * 0.5) theta = wedgeAngle - theta;

  // Convert back to cartesian.
  vec2 folded = vec2(cos(theta), sin(theta)) * r;

  // Sample the source pattern. Apply a slow time-warped coord.
  vec2 sampleCoord = folded * 4.0 + vec2(uTime * 0.3, uTime * 0.2);
  vec3 col = source(sampleCoord);

  // Brightness boost near the center.
  float centerBoost = exp(-r * 1.5);
  col *= 0.6 + centerBoost * 0.8;

  // Audio-driven color shift.
  col = mix(col, col.gbr, mid * 0.4);
  col *= 1.0 + high * 0.5;

  // Vignette.
  float vig = 1.0 - smoothstep(0.4, 1.0, r);
  col *= vig;

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeKaleidoscopeBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeKaleidoscopeRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createKaleidoscopePipeline(gl: WebGL2RenderingContext): KaleidoscopePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawKaleidoscope(
  gl: WebGL2RenderingContext,
  pipe: KaleidoscopePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
